import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { CATEGORY_COLORS, ISLAND_BY_SLUG } from "../lib/constants";
import { trackPageview } from "../lib/api";
import { useT } from "../lib/LangContext";
import { useArticleViews } from "../lib/useViews";
import { SentimentDot } from "./SentimentDot";
import { KeyFacts } from "./KeyFacts";
import { buildGoogleTranslateUrl } from "./GoogleTranslateLink";

export const ArticleCard = ({ article, index = 0, showIslandFlag = true }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { t, tCategory, timeAgo, lang } = useT();
  const viewsMap = useArticleViews();
  const views = viewsMap[article.id] || 0;
  const color = CATEGORY_COLORS[article.category] || "#0AADA0";
  const hasImage = !!article.image_url && !imgFailed;
  const islandMeta = ISLAND_BY_SLUG[article.island];

  const onCardClick = () => trackPageview("/article", article.id);

  return (
    <article
      className="card-soft overflow-hidden flex flex-col fade-up"
      style={{ animationDelay: `${Math.min(index * 80, 600)}ms` }}
      data-testid={`article-card-${article.id}`}
    >
      <Link
        to={`/article/${article.id}${article.slug ? `-${article.slug}` : ""}`}
        onClick={onCardClick}
        className="contents"
        data-testid={`article-card-link-${article.id}`}
      >
        <div className="relative w-full cursor-pointer" style={{ height: 180 }}>
          {hasImage ? (
            <img
              src={article.image_url}
              alt={article.title}
              onError={() => setImgFailed(true)}
              className="w-full h-full object-cover"
              style={{ borderRadius: "12px 12px 0 0" }}
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 60%, ${color}99 100%)`,
                borderRadius: "12px 12px 0 0",
              }}
              data-testid={`article-card-fallback-${article.id}`}
            >
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgba(255,255,255,.18) 0 2px, transparent 2px 22px)",
                }}
              />
              {article.source_logo && (
                <img
                  src={article.source_logo}
                  alt=""
                  aria-hidden="true"
                  className="absolute top-3 right-3 w-8 h-8 rounded bg-white/90 p-1 shadow-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 text-center">
                <span className="font-serif-display italic text-2xl leading-tight drop-shadow-sm">
                  {tCategory(article.category)}
                </span>
                <span className="mt-1 text-[11px] uppercase tracking-[0.2em] opacity-80">
                  {article.source_name}
                </span>
              </div>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <div className="mb-3">
          <span
            className="inline-block px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider text-white"
            style={{ background: color }}
            data-testid={`article-category-${article.id}`}
          >
            {tCategory(article.category)}
          </span>
        </div>

        <Link
          to={`/article/${article.id}${article.slug ? `-${article.slug}` : ""}`}
          onClick={onCardClick}
          className="block group"
          data-testid={`article-title-link-${article.id}`}
        >
          <h3
            className="text-[15px] text-[#1A1A1A] leading-[1.4] mb-2 group-hover:text-[#0AADA0] transition-colors"
            style={{ fontWeight: 500 }}
            data-testid={`article-title-${article.id}`}
          >
            {article.title}
          </h3>
        </Link>

        <p
          className="text-[13px] text-[#6B7280] leading-[1.6] line-clamp-3 mb-2"
          data-testid={`article-summary-${article.id}`}
        >
          {article.ai_summary}
        </p>

        {Array.isArray(article.also_from) && article.also_from.length > 0 && (
          <div
            className="flex items-center flex-wrap gap-1.5 mb-2"
            data-testid={`article-also-from-${article.id}`}
          >
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              {t("also_from")}:
            </span>
            {article.also_from.map((s) => (
              <a
                key={s.article_id}
                href={s.external_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#E5E7EB] rounded-full text-[10px] text-[#4B5563] hover:border-[#0AADA0] hover:text-[#0AADA0] transition-colors"
                data-testid={`article-also-from-source-${s.article_id}`}
                title={s.title}
              >
                {s.source_logo && (
                  <img
                    src={s.source_logo}
                    alt=""
                    className="w-3 h-3 rounded-sm"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <span className="truncate max-w-[120px]">{s.source_name}</span>
              </a>
            ))}
          </div>
        )}

        <KeyFacts
          facts={article.key_facts}
          testId={`article-card-key-facts-${article.id}`}
        />

        <div className="mt-auto pt-3 flex items-center justify-between text-[11px] text-[#6B7280] gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#0AADA0]" />
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="truncate text-[#6B7C82] hover:text-[#0AADA0]"
              data-testid={`article-source-link-${article.id}`}
              title={`Source: ${article.source_name}`}
            >
              Source: {article.source_name}
            </a>
            {showIslandFlag && islandMeta && (
              <span
                className="text-[12px] flex-shrink-0"
                title={islandMeta.name}
                aria-label={islandMeta.name}
                data-testid={`article-island-${article.id}`}
              >
                {islandMeta.flag}
              </span>
            )}
            <span className="text-[#6B7280]/60 flex-shrink-0">·</span>
            <span className="flex-shrink-0">{timeAgo(article.published_at)}</span>
            {views >= 3 && (
              <>
                <span className="text-[#6B7280]/60 flex-shrink-0">·</span>
                <span
                  className="flex-shrink-0 inline-flex items-center gap-0.5 text-[#E63946]"
                  title={`${views} reads in the last 7 days`}
                  data-testid={`article-views-${article.id}`}
                >
                  🔥 {views}
                </span>
              </>
            )}
          <SentimentDot
            sentiment={article.sentiment}
            testId={`article-sentiment-${article.id}`}
          />
        </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#EEEEEE] flex flex-col gap-1.5">
          <Link
            to={`/article/${article.id}${article.slug ? `-${article.slug}` : ""}`}
            onClick={onCardClick}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-[#E63946] hover:bg-[#cf2f3c] text-white text-[12px] font-medium transition-colors w-fit"
            data-testid={`article-read-btn-${article.id}`}
            style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
          >
            {t("read_story")} <ArrowRight size={12} />
          </Link>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[12px] text-[#0AADA0] hover:text-[#08988c] transition-colors w-fit"
            data-testid={`article-original-link-${article.id}`}
          >
            {t("read_full_article_cta")} <ExternalLink size={11} />
          </a>
          <span
            className="text-[10px] text-[#6B7C82]"
            data-testid={`article-gt-attr-${article.id}`}
          >
            {t("translated_via_google")} {article.source_name}
          </span>
        </div>
      </div>
    </article>
  );
};
